import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { setSelectedAlertID } from '@rsos/reduxDataStore/src/features/alerts/incidentsSlice';
import { setSelectedCallerID } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import { setSelectedTab } from '@rsos/reduxDataStore/src/features/eventPane/eventPaneSlice';
import {
  CHAT_TAB,
  TRANSCRIPT_TAB,
} from '@rsos/reduxDataStore/src/features/eventPane/eventPaneTabs';
import { setHighlightedFeature } from '@rsos/reduxDataStore/src/features/gisSearch/gisSearchSlice';
import { setSelectedLocation } from '@rsos/reduxDataStore/src/features/gisSearch/gisSearchSlice';
import {
  setCardsToRender,
  setMapCardData,
  AXON_MAP_CARD,
  RADE_MAP_CARD,
} from '@rsos/reduxDataStore/src/features/mapCard/mapCardSlice';
import { panToGlobalMap } from '@rsos/utils/panToGlobalMap';
import { showPinnedPopover } from '../../../Map/useDeckGL/MapPopover/mapPopoverSlice';
import { selectLookup } from '../../RADECard/radeSlice';
import type {
  AlertResult,
  AxonOfficer,
  EmergencyCallResult,
  FeatureResult,
  MessageResult,
  PlaceCandidate,
  SearchResultItem,
  SpatialResult,
  TranscriptResult,
} from './GlobalMapSearch.types';
import { getSpatialObjectCenter } from './GlobalMapSearch.utils';

interface UseResultActionsArgs {
  axonLayerData: unknown;
}

/**
 * Hook that returns click handlers for each result type. The returned
 * `handleResultClick` is a unified entry point that dispatches to the
 * type-specific handler.
 */
export default function useResultActions({
  axonLayerData,
}: UseResultActionsArgs) {
  const dispatch = useDispatch();

  const handleAxonClick = useCallback(
    (axon: AxonOfficer) => {
      try {
        const coordinates = axon.shape?.features?.[0]?.geometry?.coordinates as
          | number[]
          | undefined;
        const lng = coordinates?.[0];
        const lat = coordinates?.[1];

        if (lat != null && lng != null) {
          panToGlobalMap(lat, lng);
          dispatch(
            setHighlightedFeature({
              type: 'point' as const,
              centroid: { latitude: lat, longitude: lng },
            }),
          );
        }

        dispatch(
          setMapCardData({ layerData: axonLayerData, spatialObject: axon }),
        );
        dispatch(setCardsToRender(AXON_MAP_CARD));
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error panning to Axon coordinates', err);
      }
    },
    [dispatch, axonLayerData],
  );

  const handleFeatureClick = useCallback(
    (feature: FeatureResult) => {
      if (!feature?.geometry?.centroid) return;
      dispatch(setHighlightedFeature(feature.geometry));
    },
    [dispatch],
  );

  const applyPlaceSelection = useCallback(
    (loc: PlaceCandidate, lng: number, lat: number) => {
      dispatch(setSelectedLocation(loc));
      dispatch(showPinnedPopover({ loc }));
      dispatch(
        setHighlightedFeature({
          type: 'point' as const,
          centroid: { latitude: lat, longitude: lng },
        }),
      );
      panToGlobalMap(lat, lng);
    },
    [dispatch],
  );

  const handlePlaceClick = useCallback(
    (candidate: PlaceCandidate) => {
      if (!candidate.location) return;
      applyPlaceSelection(
        candidate,
        candidate.location.x,
        candidate.location.y,
      );
    },
    [applyPlaceSelection],
  );

  const handleSpatialClick = useCallback(
    (spatial: SpatialResult) => {
      const center = getSpatialObjectCenter(spatial.spatialObject);
      if (!center) return;
      panToGlobalMap(center.latitude, center.longitude);
      dispatch(
        setHighlightedFeature({
          type: 'point' as const,
          centroid: center,
        }),
      );
    },
    [dispatch],
  );

  const handleCallClick = useCallback(
    (call: EmergencyCallResult) => {
      // Selecting the caller is the same action the queue uses internally,
      // so it opens the CallPane and the queue takes care of panning /
      // highlighting the call's marker on the map. We intentionally don't
      // dispatch `setHighlightedFeature` or pan from here to avoid fighting
      // with that flow.
      dispatch(setSelectedCallerID(call.callId));
    },
    [dispatch],
  );

  const handleTranscriptClick = useCallback(
    (transcript: TranscriptResult) => {
      // Same first step as a call click — selecting the caller opens the
      // CallPane and fans out the usual map pan/highlight flow. We then
      // pop the user straight into the Transcript tab, which already
      // surfaces the matching tokens via its keyword-filter UI.
      dispatch(setSelectedCallerID(transcript.callId));
      dispatch(setSelectedTab(TRANSCRIPT_TAB));
    },
    [dispatch],
  );

  const handleAlertClick = useCallback(
    (alert: AlertResult) => {
      // Same action the queue uses; JurisdictionView's `selectedAlertID`
      // effect handles opening the pane.
      dispatch(setSelectedAlertID(alert.alertId));
    },
    [dispatch],
  );

  const handleRadeClick = useCallback(
    (kind: 'maf' | 'adr') => {
      // Both lookups share the RAD-e map card; `selectLookup` tells
      // RADECardNext which channel's pre-fetched data to render. The
      // background fetch already populated `mafLookup.data` /
      // `adrLookup.data` so the card has zero render-time work.
      dispatch(selectLookup(kind));
      dispatch(setMapCardData(null));
      dispatch(setCardsToRender(RADE_MAP_CARD));
    },
    [dispatch],
  );

  const handleMessageClick = useCallback(
    (message: MessageResult) => {
      // Mirror the transcript flow: select the caller (which opens the
      // CallPane and pans/highlights), then jump to the Chat tab where
      // the matching conversation lives. The chat tab itself owns which
      // sub-conversation (TF911 / collaborator / agencyShare) to show
      // based on the call's existing wiring.
      dispatch(setSelectedCallerID(message.callId));
      dispatch(setSelectedTab(CHAT_TAB));
    },
    [dispatch],
  );

  const handleResultClick = useCallback(
    (item: SearchResultItem) => {
      if (item.type === 'axon') {
        handleAxonClick(item.data);
      } else if (item.type === 'place') {
        handlePlaceClick(item.data);
      } else if (item.type === 'static' || item.type === 'dynamic') {
        handleSpatialClick(item.data);
      } else if (item.type === 'call') {
        handleCallClick(item.data);
      } else if (item.type === 'transcript') {
        handleTranscriptClick(item.data);
      } else if (item.type === 'message') {
        handleMessageClick(item.data);
      } else if (item.type === 'alert') {
        handleAlertClick(item.data);
      } else if (item.type === 'rade-maf') {
        handleRadeClick('maf');
      } else if (item.type === 'rade-adr') {
        handleRadeClick('adr');
      } else {
        handleFeatureClick(item.data);
      }
    },
    [
      handleAxonClick,
      handleFeatureClick,
      handlePlaceClick,
      handleSpatialClick,
      handleCallClick,
      handleTranscriptClick,
      handleMessageClick,
      handleAlertClick,
      handleRadeClick,
    ],
  );

  return { handleResultClick };
}
